import type { FC, ReactNode } from 'react'

import { Label } from '@/components/ui/label'

interface FormFieldProps {
    id: string
    label: string
    error?: string
    hint?: string
    optional?: boolean
    children: ReactNode
}

/*
 * Label, control, hint and error message. The control itself should set
 * aria-invalid and aria-describedby with describedBy(id, error, hint).
 */
const FormField: FC<FormFieldProps> = ({
    id,
    label,
    error,
    hint,
    optional = false,
    children
}): ReactNode => {
    return (
        <div className='space-y-2'>
            <Label htmlFor={id}>
                {label}

                {optional && (
                    <span className='font-normal text-muted-foreground'>
                        (optional)
                    </span>
                )}
            </Label>

            {children}

            {hint && !error && (
                <p id={`${id}-hint`} className='text-xs text-muted-foreground'>
                    {hint}
                </p>
            )}

            {error && (
                <p id={`${id}-error`} className='text-sm font-medium text-destructive'>
                    {error}
                </p>
            )}
        </div>
    )
}

export const describedBy = (id: string, error?: string, hint?: string): string | undefined => {
    if (error) return `${id}-error`
    if (hint) return `${id}-hint`

    return undefined
}

export default FormField
